import { Alert, Box, Button, FileInput, Flex, Modal, Table, Text } from "@mantine/core";
import { Page, PageHeader } from "../../components/layout/Page";
import {
  CheckCircleIcon,
  DownloadSimpleIcon,
  UploadSimpleIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LedgerApi } from "../../apis";
import { usePermission } from "../../components/permission";
import { IOpeningImportResult, IOpeningPreview } from "../../apis/ledger";
import { MoneyCell } from "../../components/list";
import grid from "../../components/ui/data-grid.module.css";
import { Notice } from "../../components/ui/notice";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import shared from "../shared/DataDense.module.css";
import classes from "./ledger-table.module.css";
import open from "./opening.module.css";

/**
 * Нээлтийн үлдэгдэл (SPRINT-05 §7 Алхам 3, Алхам 12) — CSV файлаас данс
 * бүрийн нээлтийн үлдэгдлийг нэг л удаа импортлоно.
 *
 * Энэ импорт нь идэвхжүүлэлт (`enabledAt`) бүрд ЗӨВХӨН НЭГ УДАА боломжтой
 * бичилт үүсгэдэг тул урьдчилан харах шаардлагатай: `POST /opening/preview`
 * нь бодит импорттой яг адилхан `parseOpeningCsv`/`balanceRows`-ыг дуудна,
 * тиймээс энд харагдах мөр, тэнцвэржүүлэх OPENING-EQUITY мөр хоёул бодит
 * импортлохоос ӨМНӨ яг тэр хэлбэрээрээ харагдана — хоёр өөр parser байхгүй.
 */
export const LedgerOpeningPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const canFinance = usePermission("FINANCE").hasAccess;

  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  const [previewData, setPreviewData] = useState<IOpeningPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const [confirmImport, setConfirmImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<IOpeningImportResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const onFileChange = (f: File | null) => {
    setFile(f);
    setCsvText(null);
    setPreviewData(null);
    setReadError(null);
    setPreviewError(null);
    if (!f) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(typeof reader.result === "string" ? reader.result : "");
    };
    reader.onerror = () => setReadError(t("billing:ledger.opening.readError"));
    reader.readAsText(f);
  };

  const runPreview = async () => {
    if (!csvText) return;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const result = await LedgerApi.previewOpeningBalance(csvText);
      setPreviewData(result);
    } catch (err) {
      message.error((err as Error).message);
      setPreviewError((err as Error).message);
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    if (!csvText) return;
    setImporting(true);
    try {
      const result = await LedgerApi.importOpeningBalance(csvText);
      setImportResult(result);
      setConfirmImport(false);
      message.success(t("billing:ledger.opening.importSuccess", { number: result.entryNumber }));
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setCsvText(null);
    setPreviewData(null);
    setImportResult(null);
    setReadError(null);
    setPreviewError(null);
  };

  const downloadTemplate = () => {
    const blob = new Blob(["role,roleRef,debit,credit\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "opening-balance-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const rows = previewData?.rows ?? [];
  const canPreview = Boolean(csvText) && !importResult;
  const canImport = Boolean(previewData) && !importResult;
  // Одоогийн алхам: 2 файл сонгох → 3 урьдчилан харах → 4 импортлох.
  const currentStep = importResult ? 5 : previewData ? 4 : file ? 3 : 2;
  const steps = [
    t("billing:ledger.opening.step1"),
    t("billing:ledger.opening.step2"),
    t("billing:ledger.opening.step3"),
    t("billing:ledger.opening.step4"),
  ];
  // Урьдчилан харах хариу мөр тус бүрийн алдаа өгдөггүй; бодитоор буруу хэлбэрийг л тэмдэглэнэ.
  const isBadRow = (r: { debit: number; credit: number }) => r.debit < 0 || r.credit < 0 || (r.debit > 0 && r.credit > 0);

  return (
    <Page>
      <PageHeader title={t("billing:ledger.opening.title")} subtitle={t("billing:ledger.opening.subtitle")} />

      {importResult && (
        <Box className={open.success} role="status">
          <CheckCircleIcon size={18} weight="bold" className={open.okIcon} />
          <Text span className={open.successTitle}>
            {t("billing:ledger.opening.importSuccess", { number: importResult.entryNumber })}
          </Text>
          <Text span className={open.successText}>
            {t("billing:ledger.opening.importSuccessLines", { count: importResult.lines })}
          </Text>
        </Box>
      )}

      <Box className={open.card}>
        <ol className={open.steps}>
          {steps.map((label, i) => {
            const n = i + 1;
            const done = n < currentStep;
            const active = n === currentStep;
            return (
              <li key={n} className={`${open.step} ${active ? open.stepActive : ""} ${done ? open.stepDone : ""}`} aria-current={active ? "step" : undefined}>
                <span className={open.stepNum}>{done ? <CheckCircleIcon size={16} weight="fill" /> : n}</span>
                <span className={open.stepLabel}>{label}</span>
              </li>
            );
          })}
        </ol>

        <Box className={open.drop}>
          <Text className={open.dropTitle}>{t("billing:ledger.opening.chooseFileLabel")}</Text>
          <Text className={open.dropHint}>{t("billing:ledger.opening.csvFormatHint")}</Text>
          <Flex gap="sm" wrap="wrap" align="center" justify="center">
            <FileInput
              value={file}
              onChange={onFileChange}
              accept=".csv,text/csv"
              placeholder={t("billing:ledger.opening.choosePlaceholder")}
              leftSection={<UploadSimpleIcon size={14} />}
              disabled={Boolean(importResult)}
              clearable
              w={280}
            />
            <Button variant="default" leftSection={<DownloadSimpleIcon size={14} />} onClick={downloadTemplate}>
              {t("billing:ledger.opening.downloadTemplate")}
            </Button>
          </Flex>
        </Box>

        {readError && <Notice tone="danger" role="alert" title={readError} />}
        {previewError && <Notice tone="danger" role="alert" title={previewError} />}

        <Flex gap="sm" justify="flex-end">
          {canFinance && (
            <Button variant="default" loading={previewing} disabled={!canPreview} onClick={runPreview}>
              {t("billing:ledger.opening.previewButton")}
            </Button>
          )}
          {!canFinance ? null : importResult ? (
            <Button variant="default" onClick={reset}>
              {t("billing:ledger.opening.resetButton")}
            </Button>
          ) : (
            <Button leftSection={<UploadSimpleIcon size={14} weight="bold" />} disabled={!canImport} onClick={() => setConfirmImport(true)}>
              {t("billing:ledger.opening.importButton")}
            </Button>
          )}
        </Flex>
      </Box>

      {previewData && (
        <Box className={grid.card}>
          <Box className={open.gridHead}>
            <Text className={open.gridTitle}>{t("billing:ledger.opening.previewedTitle")}</Text>
            <Text span className={open.chip}>{t("billing:ledger.opening.importConfirmRows", { count: rows.length })}</Text>
          </Box>
          <Box className={grid.wrap}>
            <Table className={grid.table} data-density="compact" withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("billing:ledger.opening.colRole")}</Table.Th>
                  <Table.Th>{t("billing:ledger.opening.colRoleRef")}</Table.Th>
                  <Table.Th ta="right">{t("billing:ledger.colDebit")}</Table.Th>
                  <Table.Th ta="right">{t("billing:ledger.colCredit")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text size="sm" c="dimmed" className={classes.empty}>{t("common:noData")}</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows.map((row, i) => (
                    <Table.Tr key={`${row.role}-${row.roleRef ?? ""}-${i}`} className={isBadRow(row) ? open.rowBad : undefined}>
                      <Table.Td>
                        <Text size="sm" fw={500} className={classes.code}>{row.role}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{row.roleRef || "-"}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <MoneyCell amount={row.debit > 0 ? row.debit : null} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <MoneyCell amount={row.credit > 0 ? row.credit : null} />
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}

                {previewData.balancingRow ? (
                  <Table.Tr className={classes.subtotalRow}>
                    <Table.Td>
                      <Text size="sm" fw={500} className={classes.code}>{previewData.balancingRow.role}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{t("billing:ledger.opening.balancingRowNote")}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <MoneyCell amount={previewData.balancingRow.debit > 0 ? previewData.balancingRow.debit : null} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <MoneyCell amount={previewData.balancingRow.credit > 0 ? previewData.balancingRow.credit : null} />
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  <Table.Tr className={classes.sectionRow}>
                    <Table.Td colSpan={4}>{t("billing:ledger.opening.balancingRowNone")}</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Box>
      )}

      <Modal
        opened={confirmImport}
        onClose={() => setConfirmImport(false)}
        title={t("billing:ledger.opening.importConfirmTitle")}
        size="sm">
        <Alert color="yellow" radius="md" icon={<WarningIcon size={18} weight="bold" />} mb="md">
          {t("billing:ledger.opening.importConfirmBody")}
        </Alert>
        <Box className={shared.card} mb="md">
          <Box className={shared.filterBar}>
            <Text size="sm" c="dimmed">{t("billing:ledger.opening.previewedTitle")}</Text>
            <Box flex={1} />
            <Text size="sm" fw={500}>
              {t("billing:ledger.opening.importConfirmRows", { count: rows.length })}
            </Text>
          </Box>
          {previewData?.balancingRow && (
            <Box className={shared.filterBar}>
              <Text size="sm" c="dimmed">{t("billing:ledger.report.difference")}</Text>
              <Box flex={1} />
              <Text size="sm" fw={500}>
                {currencyFormat(previewData.balancingRow.debit || previewData.balancingRow.credit)}
              </Text>
            </Box>
          )}
        </Box>
        <Flex justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={() => setConfirmImport(false)} disabled={importing}>
            {t("common:cancel")}
          </Button>
          <Button loading={importing} onClick={runImport}>
            {t("billing:ledger.opening.importButton")}
          </Button>
        </Flex>
      </Modal>
    </Page>
  );
};

export default LedgerOpeningPage;
